import React, { useRef, useState, useEffect } from 'react';
import { Camera, Upload, Zap, X, Check, RefreshCw, ScanLine, AlertCircle, Sparkles, ArrowRight, ChevronLeft, ChevronRight, Layers, Eye, ExternalLink, Shield, BarChart3, Wand2, CreditCard, GraduationCap, Grid3X3 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { client, ximilarFetch } from '../api/client';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  show: { opacity: 1, scale: 1, transition: { duration: 0.3 } }
};

// Card Details Modal with Price Comparison View
const CardDetailsModal = ({
  card,
  onClose,
  parallels,
  currentParallelIndex,
  setCurrentParallelIndex
}: {
  card: any;
  onClose: () => void;
  parallels: any[];
  currentParallelIndex: number;
  setCurrentParallelIndex: (i: number) => void;
}) => {
  const currentCard = parallels.length > 0 ? parallels[currentParallelIndex] : card;
  // Prices come inline from Ximilar identification — no separate fetch needed
  const prices = card.prices || null;
  const loadingPrices = false;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/90 backdrop-blur-xl flex flex-col"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <button onClick={onClose} className="p-2 bg-white/10 rounded-xl">
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>
        <h2 className="text-white font-semibold">Card Details</h2>
        <div className="w-9" />
      </div>

      {/* Card Image with Parallels Navigation */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 relative">
        {parallels.length > 1 && (
          <>
            <button
              onClick={() => setCurrentParallelIndex(Math.max(0, currentParallelIndex - 1))}
              disabled={currentParallelIndex === 0}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 rounded-full disabled:opacity-30"
            >
              <ChevronLeft className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={() => setCurrentParallelIndex(Math.min(parallels.length - 1, currentParallelIndex + 1))}
              disabled={currentParallelIndex === parallels.length - 1}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 rounded-full disabled:opacity-30"
            >
              <ChevronRight className="w-5 h-5 text-white" />
            </button>
          </>
        )}

        <motion.div
          key={currentParallelIndex}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="w-64 h-80 rounded-2xl overflow-hidden border border-white/20 shadow-2xl"
        >
          <img
            src={currentCard.image_url || card.image_url}
            alt={currentCard.name || card.name}
            className="w-full h-full object-cover"
          />
        </motion.div>

        {/* Parallels indicator */}
        {parallels.length > 1 && (
          <div className="flex items-center gap-2 mt-4">
            <Layers className="w-4 h-4 text-copper-400" />
            <span className="text-cream-200/60 text-sm">
              {currentParallelIndex + 1} of {parallels.length} Parallels
            </span>
          </div>
        )}

        {/* Dots indicator */}
        {parallels.length > 1 && (
          <div className="flex gap-1.5 mt-3">
            {parallels.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentParallelIndex(i)}
                className={`w-2 h-2 rounded-full transition-colors ${i === currentParallelIndex ? 'bg-copper-400' : 'bg-white/20'
                  }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Details Sheet */}
      <div className="bg-carbon/95 backdrop-blur-xl rounded-t-3xl p-6 space-y-4 border-t border-white/10 max-h-[50vh] overflow-y-auto">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-1 bg-copper-500/20 text-copper-400 text-[10px] font-bold rounded-lg uppercase tracking-wide">
              {currentCard.grade || card.grade || 'Raw'}
            </span>
            {currentCard.parallelName && (
              <span className="px-2 py-1 bg-sage-500/20 text-sage-400 text-[10px] font-bold rounded-lg uppercase tracking-wide">
                {currentCard.parallelName}
              </span>
            )}
          </div>
          <h3 className="text-xl font-display font-semibold text-cream-50">{currentCard.name || card.name}</h3>
          <p className="text-cream-200/50 text-sm">{currentCard.set_name || card.set_name}</p>
        </div>

        {/* Price Comparison Grid */}
        <div className="grid grid-cols-3 gap-2">
          <div className="card-premium p-3 text-center">
            <p className="text-cream-200/40 text-[10px] uppercase tracking-wide mb-1">Raw</p>
            <p className="text-lg font-display font-semibold text-cream-50">
              {loadingPrices ? '...' : prices?.raw ? `$${prices.raw}` : (card.price || '$0')}
            </p>
          </div>
          <div className="card-premium p-3 text-center border border-sage-500/20">
            <p className="text-sage-400 text-[10px] uppercase tracking-wide mb-1">PSA 9</p>
            <p className="text-lg font-display font-semibold text-cream-50">
              {loadingPrices ? '...' : prices?.['psa-9'] ? `$${prices['psa-9']}` : '—'}
            </p>
          </div>
          <div className="card-premium p-3 text-center border border-copper-400/20">
            <p className="text-copper-400 text-[10px] uppercase tracking-wide mb-1">PSA 10</p>
            <p className="text-lg font-display font-semibold text-cream-50">
              {loadingPrices ? '...' : prices?.['psa-10'] ? `$${prices['psa-10']}` : '—'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="card-premium p-3">
            <p className="text-cream-200/40 text-[10px] uppercase tracking-wide mb-1">Parallels</p>
            <p className="text-xl font-display font-semibold text-cream-50">{parallels.length || card.parallelsCount || 1}</p>
          </div>
          <div className="card-premium p-3">
            <p className="text-cream-200/40 text-[10px] uppercase tracking-wide mb-1">Graded Premium</p>
            <p className="text-xl font-display font-semibold text-success">
              {prices?.raw && prices?.['psa-10']
                ? `${((parseFloat(prices['psa-10']) / parseFloat(prices.raw) - 1) * 100).toFixed(0)}%`
                : '—'
              }
            </p>
          </div>
        </div>

        <button
          onClick={onClose}
          className="w-full py-4 bg-copper-500 hover:bg-copper-400 text-cream-50 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
        >
          Done
        </button>
      </div>
    </motion.div>
  );
};

export const Scanner = () => {
  const scannerNavigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [image, setImage] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [flashMode, setFlashMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedCards, setSavedCards] = useState<Set<number>>(new Set());

  // AI Grading state (Ximilar)
  const [gradingOpen, setGradingOpen] = useState(false);
  const [gradingCardIndex, setGradingCardIndex] = useState<number | null>(null);
  const [gradeResult, setGradeResult] = useState<any>(null);
  const [loadingGrades, setLoadingGrades] = useState(false);

  // Card details modal state
  const [selectedCard, setSelectedCard] = useState<any>(null);
  const [cardParallels, setCardParallels] = useState<any[]>([]);
  const [currentParallelIndex, setCurrentParallelIndex] = useState(0);

  // Ximilar per-scan feature flags (all off by default to save credits)
  const [featureFlags, setFeatureFlags] = useState({
    magic_ai: false,
    slab_id: false,
    slab_grade: false,
    analyze_all: false,
  });

  const toggleFlag = (flag: keyof typeof featureFlags) => {
    setFeatureFlags(prev => ({ ...prev, [flag]: !prev[flag] }));
  };

  const resetFlags = () => {
    setFeatureFlags({ magic_ai: false, slab_id: false, slab_grade: false, analyze_all: false });
  };

  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, []);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          // @ts-ignore - torch is not in standard types yet
          advanced: [{ torch: flashMode }]
        }
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setError(null);
    } catch (err) {
      console.error("Camera error:", err);
      setError("Could not access camera. Please check permissions.");
    }
  };

  const toggleFlash = async () => {
    if (stream) {
      const track = stream.getVideoTracks()[0];
      try {
        await track.applyConstraints({
          advanced: [{ torch: !flashMode } as any]
        });
        setFlashMode(!flashMode);
      } catch (err) {
        console.error("Flash toggle failed", err);
      }
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const capture = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;

      const MAX_WIDTH = 800;
      let width = video.videoWidth;
      let height = video.videoHeight;

      if (width > MAX_WIDTH) {
        height = height * (MAX_WIDTH / width);
        width = MAX_WIDTH;
      }

      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d')?.drawImage(video, 0, 0, width, height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      setImage(dataUrl);
      analyzeCard(dataUrl, featureFlags);
    }
  };

  const openCardDetails = (card: any) => {
    setSelectedCard(card);
    setCurrentParallelIndex(0);
    // Ximilar doesn't provide parallels data — show the base card
    setCardParallels([{ ...card, parallelName: 'Base' }]);
  };

  // AI Grading flow (Ximilar)
  const openAIGrading = async (index: number) => {
    setGradingCardIndex(index);
    setGradeResult(null);
    setGradingOpen(true);
    setLoadingGrades(true);

    try {
      // Use the captured image for AI grading
      const cardImage = image; // the captured camera image
      if (!cardImage) throw new Error("No image available for grading");

      const res = await ximilarFetch('/api/grade', {
        method: 'POST',
        body: JSON.stringify({ image: cardImage, mode: 'detailed' }),
      });

      if (res.ok) {
        const data = await res.json();
        setGradeResult(data.data);

        // Auto-apply overall grade to the card
        if (data.data?.overall && index !== null && result) {
          const gradeLabel = `AI: ${data.data.overall}`;
          const updated = [...result];
          updated[index] = { ...updated[index], grade: gradeLabel };
          setResult(updated);
        }
      } else {
        const errData = await res.json().catch(() => ({}));
        setGradeResult({ error: errData.error || 'Grading failed' });
      }
    } catch (e: any) {
      console.error("AI grading failed:", e);
      setGradeResult({ error: e.message || 'Grading failed' });
    } finally {
      setLoadingGrades(false);
    }
  };

  const saveCardToCollection = async (card: any, index: number) => {
    setSaving(true);
    try {
      const sessionResult = await client.auth.getSession();
      const sessionUser = sessionResult?.data?.user;
      if (!sessionUser) throw new Error("Please log in to save cards.");

      // Upload the captured image to Supabase Storage if it's a base64 data URL
      let imageUrl = card.image_url || '';
      if (imageUrl && imageUrl.startsWith('data:')) {
        try {
          // Convert base64 to Blob
          const res = await fetch(imageUrl);
          const blob = await res.blob();
          const ext = blob.type.split('/')[1] || 'jpeg';
          const filePath = `${sessionUser.id}/${Date.now()}.${ext}`;

          const { error: uploadError } = await supabase.storage
            .from('card-images')
            .upload(filePath, blob, { upsert: true });

          if (!uploadError) {
            const { data: urlData } = supabase.storage
              .from('card-images')
              .getPublicUrl(filePath);
            imageUrl = urlData.publicUrl;
          } else {
            console.warn('Image upload failed, saving without image:', uploadError);
            imageUrl = '';
          }
        } catch (uploadErr) {
          console.warn('Image upload error:', uploadErr);
          imageUrl = '';
        }
      }

      const { error } = await supabase.from('cards').insert({
        user_id: sessionUser.id,
        name: card.name,
        set_name: card.set_name,
        grade: card.grade,
        price: card.priceNum || 0,
        image_url: imageUrl,
        status: 'collection',
        catalog_card_id: card.id || null
      });

      if (error) throw new Error(error.message);

      setSavedCards(prev => new Set(prev).add(index));
      setError(null);
    } catch (e: any) {
      console.error("Failed to save card:", e);
      setError(e?.message || 'Failed to save card to your collection.');
    } finally {
      setSaving(false);
    }
  };

  const analyzeCard = async (img: string, options?: typeof featureFlags) => {
    setAnalyzing(true);
    setError(null);
    setSavedCards(new Set());

    try {
      const response = await ximilarFetch('/api/identify', {
        method: 'POST',
        body: JSON.stringify({ image: img, options }),
      });

      if (!response.ok) {
        const errText = await response.text();
        try {
          const errJson = JSON.parse(errText);
          throw new Error(errJson.error || "Failed to identify card");
        } catch (e) {
          throw new Error(`Server Error (${response.status}): ${errText.slice(0, 100)}`);
        }
      }

      const responseData = await response.json();

      const apiData = responseData.data;
      let detections: any[] = [];

      // Identify response: { success, detections: [...], processingTime }
      if (apiData && apiData.detections && Array.isArray(apiData.detections) && apiData.detections.length > 0) {
        detections = apiData.detections;
      } else if (Array.isArray(apiData)) {
        detections = apiData.map((d: any) => ({ card: d, confidence: "Medium" }));
      } else if (apiData) {
        detections = [{ card: apiData, confidence: "Medium" }];
      }

      if (detections.length === 0) {
        throw new Error("No card details found in the response.");
      }

      const processedResults = detections.map((detection: any) => {
        const cardData = detection.card || {};
        const confidence = detection.confidence || 0;

        // Ximilar sport_id returns prices inline from pricing.list[]
        const prices = cardData.prices || {};
        const priceNum = parseFloat(prices.raw) || 0;

        // sport_id doesn't return card images; use the captured photo
        const imageUrl = cardData.image_url || cardData.imageUrl || '';

        return {
          id: cardData.id,
          name: cardData.name || "Unknown Card",
          set_name: cardData.set_name || cardData.setName || cardData.set || "Unknown Set",
          year: cardData.year || null,
          cardNumber: cardData.card_number || cardData.cardNumber || null,
          company: cardData.company || null,
          subcategory: cardData.subcategory || null,
          cardType: cardData.card_type || cardData.cardType || null,
          grade: cardData.grade || "Raw",
          grader: cardData.grader || null,
          certNumber: cardData.cert_number || null,
          priceNum,
          price: priceNum > 0 ? `$${priceNum.toFixed(2)}` : "$0.00",
          prices,
          confidence: typeof confidence === 'number' ? Math.round(confidence * 100) : 80,
          image_url: imageUrl || img,
          status: 'collection',
          alternatives: detection.alternatives || [],
          marketplaceListings: cardData.marketplaceListings || [],
          ebayLink: cardData.links?.ebay || cardData.ebayLink || null,
          comcLink: cardData.links?.comc || cardData.comcLink || null,
          beckettLink: cardData.links?.beckett || cardData.beckettLink || null,
          isAutograph: cardData.is_autographed || cardData.isAutograph || false,
          isFoil: cardData.is_foil || cardData.isFoil || false,
          source: 'ximilar'
        };
      });

      setResult(processedResults);

    } catch (err: any) {
      console.error("Analysis error:", err);
      if (err.message === "Failed to fetch" || err.message.includes("Load failed")) {
        setError("Network error. Please check your connection or try again.");
      } else {
        setError(err.message || "Could not identify card. Try again.");
      }
    } finally {
      setAnalyzing(false);
      resetFlags();
    }
  };

  const reset = () => {
    setImage(null);
    setResult(null);
    setError(null);
    startCamera();
  };

  return (
    <div className="fixed inset-0 flex flex-col bg-obsidian overflow-hidden" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 24px)' }}>
      {/* Background gradient */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-gradient-radial from-copper-500/10 to-transparent rounded-full blur-3xl -translate-y-1/2" />
      </div>

      {/* Camera View */}
      <div className="flex-1 relative">
        {!image && (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="w-full h-full object-cover"
          />
        )}

        {image && (
          <motion.img
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            src={image}
            alt="Captured"
            className="w-full h-full object-cover"
          />
        )}

        <canvas ref={canvasRef} className="hidden" />

        {/* Overlay UI */}
        <div className="absolute inset-0 pointer-events-none flex flex-col justify-between p-5 z-10">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-between items-start pt-10"
          >
            <div>
              <h1 className="text-cream-50 font-display font-semibold text-2xl drop-shadow-lg tracking-tight">Card Scanner</h1>
              <p className="text-cream-200/50 text-xs font-medium mt-0.5">Powered by Ximilar AI</p>
            </div>
            <div className="flex gap-2 pointer-events-auto">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={toggleFlash}
                className={`p-3 rounded-xl backdrop-blur-xl border transition-all duration-300 ${flashMode
                  ? 'bg-warning/90 text-obsidian border-warning/50'
                  : 'bg-obsidian/60 text-cream-100 border-white/10 hover:bg-obsidian/80'
                  }`}
              >
                <Zap size={18} fill={flashMode ? "currentColor" : "none"} />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={reset}
                className="p-3 bg-obsidian/60 backdrop-blur-xl rounded-xl border border-white/10 text-cream-100 pointer-events-auto hover:bg-obsidian/80 transition-colors"
              >
                <RefreshCw size={18} />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => scannerNavigate(-1)}
                className="p-3 bg-obsidian/60 backdrop-blur-xl rounded-xl border border-white/10 text-cream-100 pointer-events-auto hover:bg-danger/80 transition-colors"
                aria-label="Close scanner"
              >
                <X size={18} />
              </motion.button>
            </div>
          </motion.div>

          {/* Scanning Frame - Flexbox Centering */}
          <AnimatePresence>
            {!image && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none pb-20">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="w-72 h-96 relative"
                >
                  {/* Corner brackets */}
                  <div className="absolute top-0 left-0 w-8 h-8 border-l-2 border-t-2 border-copper-400 rounded-tl-xl" />
                  <div className="absolute top-0 right-0 w-8 h-8 border-r-2 border-t-2 border-copper-400 rounded-tr-xl" />
                  <div className="absolute bottom-0 left-0 w-8 h-8 border-l-2 border-b-2 border-copper-400 rounded-bl-xl" />
                  <div className="absolute bottom-0 right-0 w-8 h-8 border-r-2 border-b-2 border-copper-400 rounded-br-xl" />

                  {/* Scanning line */}
                  <motion.div
                    className="absolute left-2 right-2 h-0.5 bg-gradient-to-r from-transparent via-copper-400 to-transparent rounded-full"
                    style={{ boxShadow: '0 0 20px rgba(201, 145, 93, 0.6)' }}
                    animate={{ y: [0, 380, 0] }}
                    transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
                  />

                  {/* Center hint */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <Sparkles className="w-6 h-6 text-copper-400/60 mx-auto mb-2" />
                      <p className="text-cream-100/40 text-xs font-medium">Center card in frame</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>

          {/* Feature Toggle Buttons + Capture Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center gap-3 pointer-events-auto" style={{ paddingBottom: 'calc(2rem + max(env(safe-area-inset-bottom, 0px), 24px))' }}
          >
            {/* Feature flag toggles — only show in camera mode */}
            {!image && (
              <div className="flex gap-2 flex-wrap justify-center">
                {([
                  { key: 'magic_ai', label: 'Magic AI', Icon: Wand2 },
                  { key: 'slab_id', label: 'Slab ID', Icon: CreditCard },
                  { key: 'slab_grade', label: 'Slab Grade', Icon: GraduationCap },
                  { key: 'analyze_all', label: 'All Cards', Icon: Grid3X3 },
                ] as const).map(({ key, label, Icon }) => {
                  const active = featureFlags[key];
                  return (
                    <motion.button
                      key={key}
                      whileTap={{ scale: 0.92 }}
                      onClick={() => toggleFlag(key)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 backdrop-blur-md ${active
                          ? 'bg-copper-500/90 border-copper-400/80 text-cream-50 shadow-[0_0_12px_rgba(201,145,93,0.5)]'
                          : 'bg-obsidian/60 border-white/10 text-cream-200/50 hover:border-white/20 hover:text-cream-200/80'
                        }`}
                    >
                      <Icon className="w-3 h-3" />
                      {label}
                    </motion.button>
                  );
                })}
              </div>
            )}

            {!image ? (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.9 }}
                onClick={capture}
                className="relative group"
              >
                {/* Outer ring */}
                <div className="w-20 h-20 rounded-full border-[3px] border-cream-100/80 flex items-center justify-center bg-obsidian/20 backdrop-blur-sm">
                  {/* Inner button */}
                  <div className="w-16 h-16 rounded-full bg-cream-50 group-hover:bg-copper-400 transition-colors duration-300" />
                </div>
                {/* Glow effect */}
                <div className="absolute inset-0 rounded-full bg-copper-400/20 blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.button>
            ) : (
              <AnimatePresence>
                {analyzing && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    className="flex items-center gap-3 px-6 py-4 bg-obsidian/80 backdrop-blur-xl rounded-2xl border border-copper-400/30"
                  >
                    <div className="relative">
                      <ScanLine className="w-5 h-5 text-copper-400 animate-pulse" />
                      <div className="absolute inset-0 animate-spin">
                        <div className="w-5 h-5 border-2 border-copper-400/30 border-t-copper-400 rounded-full" />
                      </div>
                    </div>
                    <span className="text-cream-100 font-medium">Analyzing card...</span>
                  </motion.div>
                )}
              </AnimatePresence>
            )}
          </motion.div>
        </div>
      </div>

      {/* Result Sheet */}
      <AnimatePresence>
        {result && result.length > 0 && (
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="absolute bottom-0 left-0 right-0 bg-carbon/98 backdrop-blur-2xl rounded-t-[2rem] border-t border-white/[0.08] z-20 max-h-[85vh] overflow-hidden"
          >
            {/* Handle */}
            <div className="pt-4 pb-2">
              <div className="w-10 h-1 bg-white/20 rounded-full mx-auto" />
            </div>

            {/* Content */}
            <div className="px-6 overflow-y-auto max-h-[70vh]" style={{ paddingBottom: 'calc(2rem + max(env(safe-area-inset-bottom, 0px), 24px))' }}>
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 rounded-xl bg-success/20 flex items-center justify-center">
                  <Check className="w-4 h-4 text-success" />
                </div>
                <div>
                  <h2 className="text-cream-50 font-display font-semibold text-lg">Card Identified</h2>
                  <p className="text-cream-200/40 text-xs">Tap save to add to your collection</p>
                </div>
              </div>

              <div className="space-y-4">
                {result.map((card: any, index: number) => (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    initial="hidden"
                    animate="show"
                    transition={{ delay: index * 0.1 }}
                    onClick={() => openCardDetails(card)}
                    className="flex gap-4 p-4 card-premium cursor-pointer active:scale-[0.98] transition-transform"
                  >
                    <div className="w-24 h-32 rounded-xl overflow-hidden border border-white/10 flex-shrink-0 bg-obsidian">
                      <img src={card.image_url} className="w-full h-full object-cover" alt={card.name} />
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col justify-between py-1">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-1 bg-copper-500/20 text-copper-400 text-[10px] font-bold rounded-lg uppercase tracking-wide">
                            {card.grade}
                          </span>
                          <span className="text-success text-[10px] font-bold flex items-center gap-1">
                            <span className="w-1.5 h-1.5 bg-success rounded-full animate-pulse" />
                            {card.confidence}% Match
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-cream-50 leading-tight mb-1 truncate">{card.name}</h3>
                        <p className="text-cream-200/50 text-sm truncate">
                          {[card.set_name, card.company, card.year].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-2xl font-display font-semibold text-cream-50">{card.price}</span>
                        <div className="flex items-center gap-1.5 flex-wrap justify-end">
                          <button
                            onClick={(e) => { e.stopPropagation(); openAIGrading(index); }}
                            className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-sage-500/15 hover:bg-sage-500/25 text-sage-400 transition-colors flex items-center gap-1 border border-sage-500/20"
                          >
                            <Shield className="w-3 h-3" />
                            {card.grade !== 'Raw' ? card.grade : 'AI Grade'}
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); saveCardToCollection(card, index); }}
                            disabled={saving || savedCards.has(index)}
                            className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1 ${savedCards.has(index)
                              ? 'bg-success/20 text-success'
                              : 'bg-copper-500 hover:bg-copper-400 text-cream-50'
                              }`}
                          >
                            {savedCards.has(index) ? <Check className="w-3 h-3" /> : null}
                            {savedCards.has(index) ? 'Saved' : 'Save'}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (card.ebayLink) {
                                window.open(card.ebayLink, '_blank');
                              } else {
                                const query = encodeURIComponent(`${card.name} ${card.set_name}`.trim());
                                window.open(`https://www.ebay.com/sch/i.html?_nkw=${query}&_sacat=212`, '_blank');
                              }
                            }}
                            className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-white/[0.06] hover:bg-white/[0.12] text-cream-100 transition-colors flex items-center gap-1 border border-white/[0.08]"
                          >
                            <ExternalLink className="w-3 h-3" />
                            eBay
                          </button>
                          <div className="flex items-center gap-1 text-copper-400">
                            <Eye className="w-4 h-4" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-6">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={reset}
                  className="flex-1 py-4 bg-white/[0.05] hover:bg-white/[0.08] text-cream-100 rounded-xl font-semibold transition-colors border border-white/[0.06]"
                >
                  Scan Another
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02, x: 2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => window.location.href = '/collection'}
                  className="flex-1 py-4 bg-copper-500 hover:bg-copper-400 text-cream-50 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  View Collection
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Card Details Modal */}
      <AnimatePresence>
        {selectedCard && (
          <CardDetailsModal
            card={selectedCard}
            parallels={cardParallels}
            currentParallelIndex={currentParallelIndex}
            setCurrentParallelIndex={setCurrentParallelIndex}
            onClose={() => {
              setSelectedCard(null);
              setCardParallels([]);
            }}
          />
        )}
      </AnimatePresence>

      {/* AI Grading Results Modal */}
      <AnimatePresence>
        {gradingOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end justify-center"
            onClick={() => setGradingOpen(false)}
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-carbon rounded-t-3xl w-full max-w-lg p-6 border-t border-white/10 max-h-[70vh] overflow-y-auto"
            >
              <div className="w-10 h-1 bg-white/20 rounded-full mx-auto mb-6" />
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-sage-500/20 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-sage-400" />
                </div>
                <div>
                  <h3 className="text-lg font-display font-semibold text-cream-50">AI Card Grading</h3>
                  <p className="text-cream-200/40 text-xs">Powered by Ximilar AI Vision</p>
                </div>
              </div>

              {/* Loading State */}
              {loadingGrades && (
                <div className="flex flex-col items-center justify-center py-8 gap-4">
                  <div className="relative">
                    <div className="w-12 h-12 border-3 border-copper-400/20 border-t-copper-400 rounded-full animate-spin" />
                    <Shield className="w-5 h-5 text-copper-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                  </div>
                  <p className="text-cream-200/60 text-sm">Analyzing card condition...</p>
                  <p className="text-cream-200/30 text-xs">This takes 8-20 seconds for detailed analysis</p>
                </div>
              )}

              {/* Error State */}
              {!loadingGrades && gradeResult?.error && (
                <div className="bg-danger/10 border border-danger/20 rounded-xl p-4 mb-4">
                  <p className="text-danger text-sm">{gradeResult.error}</p>
                </div>
              )}

              {/* Grade Results */}
              {!loadingGrades && gradeResult && !gradeResult.error && (
                <div className="space-y-4">
                  {/* Overall Grade */}
                  <div className="bg-gradient-to-br from-copper-500/10 to-sage-500/10 rounded-2xl p-5 text-center border border-copper-400/20">
                    <p className="text-cream-200/50 text-xs uppercase tracking-wide mb-2">Overall Condition</p>
                    <p className="text-4xl font-display font-bold text-copper-400">{gradeResult.overall || 'N/A'}</p>
                    {gradeResult.confidence && (
                      <p className="text-cream-200/40 text-xs mt-1">{Math.round(gradeResult.confidence * 100)}% confidence</p>
                    )}
                  </div>

                  {/* Sub-Grades Grid */}
                  {gradeResult.subGrades && Object.keys(gradeResult.subGrades).length > 0 && (
                    <div>
                      <p className="text-cream-200/50 text-xs uppercase tracking-wide mb-3">Detailed Sub-Grades</p>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(gradeResult.subGrades).map(([key, value]: [string, any]) => (
                          <div key={key} className="card-premium p-3 text-center">
                            <p className="text-cream-200/40 text-[10px] uppercase tracking-wide mb-1">{key}</p>
                            <p className="text-lg font-display font-semibold text-cream-50">
                              {typeof value === 'number' ? value.toFixed(1) : String(value)}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    onClick={() => setGradingOpen(false)}
                    className="w-full py-3 bg-copper-500 hover:bg-copper-400 text-cream-50 rounded-xl font-semibold transition-colors mt-4"
                  >
                    Done
                  </button>
                </div>
              )}

              {/* No result yet and not loading */}
              {!loadingGrades && !gradeResult && (
                <p className="text-cream-200/40 text-sm text-center py-6">No grading data available</p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Toast */}
      <AnimatePresence>
        {error && !result && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            className="absolute top-28 left-4 right-4 bg-danger/95 backdrop-blur-xl text-cream-50 p-4 rounded-2xl flex items-center gap-3 z-30 border border-danger/50 shadow-elevated"
          >
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
              <AlertCircle size={20} />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm">Scan Failed</p>
              <p className="text-sm opacity-80">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <X size={18} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
